import { ArrowLeft } from 'lucide-react';

import {
  type ItemAvailability,
  REST_LABEL_KEY,
  type RestPartKind,
  formatRestAmount,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import {
  type ExpenseLineItem,
  type LineItemsRest,
  type UnpickedSplit,
  formatAmountForCurrency,
  formatCurrencyWithCode,
} from '@splitfiesta/shared';
import { Alert, Button } from '@splitfiesta/ui';

import { LineItemRow } from '@/components/expenses/lineItems/LineItemRow';
import { RestRow } from '@/components/expenses/lineItems/RestRow';

export interface ClaimListProps {
  /** The person whose picks these are. */
  name: string;
  title: string;
  payer: string;
  method: UnpickedSplit['method'];
  /** `takenBy` in the order the names should read. */
  rows: Array<{ item: ExpenseLineItem; availability: ItemAvailability }>;
  nameOf: (userId: string) => string;
  /** The receipt's currency. */
  currency: string;
  rest: {
    kind: 'rest' | 'restNegative';
    mode: LineItemsRest['mode'];
    amount: number;
  } | null;
  footer: {
    items: number;
    itemsAmount: number;
    /** Their part of the tip, tax and units nobody picked yet; null when nothing is left over. */
    rest: { kind: RestPartKind; amount: number } | null;
  } | null;
  total: { amount: number; currency: string };
  /** The total in the group currency, only when the bill is foreign. */
  groupShare?: { amount: number; currency: string };
  canSave: boolean;
  saving: boolean;
  error: string | null;
  onToggle: (itemId: string) => void;
  onUnits: (itemId: string, units: number) => void;
  onSave: () => void;
  onBack: () => void;
}

/** The link's claim list: the detail's rows and footer, in the link's words. */
export function ClaimList({
  name,
  title,
  payer,
  method,
  rows,
  nameOf,
  currency,
  rest,
  footer,
  total,
  groupShare,
  canSave,
  saving,
  error,
  onToggle,
  onUnits,
  onSave,
  onBack,
}: ClaimListProps) {
  const { t } = useTranslation('claimLink');
  const totalLabel = formatCurrencyWithCode(total.amount, total.currency);

  const captionOf = (availability: ItemAvailability) => {
    if (availability.kind === 'free') return t(`claim.nobodyYet.${method}`);
    if (availability.kind === 'taken' && availability.takenBy.length === 1)
      return t('claim.theirs', { name: nameOf(availability.takenBy[0]) });
    return undefined;
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        disabled={saving}
        className="-ml-2 inline-flex min-h-11 items-center gap-1 rounded-lg px-2 text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-300 dark:hover:text-gray-100"
      >
        <ArrowLeft aria-hidden="true" className="h-4 w-4" />
        {t('claim.back')}
      </button>
      <p className="mt-2 break-words text-sm text-gray-600 dark:text-gray-400">
        {t('claim.bill', { title, payer })}
      </p>
      <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-50">
        {t('claim.title', { name })}
      </h1>
      <p className="mt-1 text-base text-gray-700 dark:text-gray-300">
        {t(`claim.lead.${method}`)}
      </p>

      {error ? <Alert type="error" message={error} className="mt-3" /> : null}

      <ul className="mt-3">
        {rows.map(({ item, availability }) => (
          <LineItemRow
            key={item.id}
            item={item}
            availability={availability}
            nameOf={nameOf}
            currency={currency}
            caption={captionOf(availability)}
            disabled={saving}
            onToggle={() => onToggle(item.id)}
            onUnits={units => onUnits(item.id, units)}
          />
        ))}
      </ul>

      {rest ? (
        <RestRow
          testId="claim-link-rest"
          label={t(
            rest.kind === 'restNegative'
              ? 'expenses:lineItems.restNegative'
              : 'expenses:lineItems.rest'
          )}
          amount={formatAmountForCurrency(rest.amount, currency)}
          mode={rest.mode}
        />
      ) : null}

      <div className="sticky bottom-0 -mx-4 mt-4 border-t border-gray-200 bg-white px-4 pb-4 pt-3 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-[45%] flex-1 flex-wrap items-baseline gap-x-2">
            {footer ? (
              <p
                data-testid="claim-link-footer-line"
                className="w-full text-sm text-gray-600 dark:text-gray-400"
              >
                {t('claim.footer.items', {
                  count: footer.items,
                  amount: formatAmountForCurrency(footer.itemsAmount, currency),
                })}
                {footer.rest
                  ? ` · ${t(
                      `claim.footer.${REST_LABEL_KEY[footer.rest.kind]}`,
                      {
                        amount: formatRestAmount(footer.rest.amount, currency),
                      }
                    )}`
                  : null}
              </p>
            ) : null}
            <span className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100">
              {totalLabel}
            </span>
            {groupShare ? (
              <span className="text-sm tabular-nums text-gray-500 dark:text-gray-400">
                {formatCurrencyWithCode(groupShare.amount, groupShare.currency)}
              </span>
            ) : null}
          </div>
          <Button
            type="button"
            variant="primary"
            onClick={onSave}
            disabled={!canSave || saving}
            loading={saving}
            className="ml-auto min-h-11"
          >
            {t('claim.save', { total: totalLabel })}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ClaimList;
